import classNames from "classnames";
import React, { useLayoutEffect, useRef, useState } from "react";
import usePrefersReducedMotion from "../../../shared/hooks/usePrefersReducedMotion";
import useSlidingIndicator, { INDICATOR_STATE_ATTRIBUTE } from "../../../shared/hooks/useSlidingIndicator";
import "./kuika-selection-indicator.scss";

/**
 * A row marked `data-selected="true"` (KuikaMenuItem does) that is not inside a closed or closing
 * KuikaCollapsePanel — rc-collapse marks the content `-inactive` as soon as it starts to close.
 */
export const DEFAULT_SELECTION_SELECTOR: string = '[data-selected="true"]:not(.rc-collapse-content-inactive *)';

export interface IKuikaSelectionIndicatorProps {
  /** Finds the selected row inside the container. Defaults to `DEFAULT_SELECTION_SELECTOR`. */
  activeSelector?: string;
  /** Hides the indicator, e.g. while react-beautiful-dnd or rc-tree drags a row out from under it. */
  hidden?: boolean;
  /**
   * Indicators that share this key continue from one another. A route change that unmounts a list
   * and mounts it again (the management rail and settings menu are rendered per page) would place
   * the new indicator at once; with a key it slides from where the old one stood.
   */
  continuityKey?: string;
  className?: string;
}

// The selection is read from the DOM, so the hook's key only has to be present; the selector finds the row.
const SELECTION_KEY: string = "selection";
const WATCHED_ATTRIBUTES: string[] = ["class", "data-selected", "aria-current"];

// ── Continuity across remounts ─────────────────────────────────────────────────────────────────

const GEOMETRY_PROPERTIES: string[] = ["--indicator-x", "--indicator-y", "--indicator-w", "--indicator-h"];
// A list remounted by a route change comes back once the next page has loaded, well within this;
// a geometry older than that belongs to an earlier visit and is not slid from.
const CONTINUITY_WINDOW_MS: number = 3000;

interface IRememberedGeometry {
  values: string[];
  at: number;
}

const rememberedGeometries: Map<string, IRememberedGeometry> = new Map<string, IRememberedGeometry>();

/** Keeps where a shown indicator stood as its list unmounts. */
const rememberGeometry = (key: string, container: HTMLElement): void => {
  if (container.getAttribute(INDICATOR_STATE_ATTRIBUTE) !== "animate") {
    rememberedGeometries.delete(key);
    return;
  }

  rememberedGeometries.set(key, {
    values: GEOMETRY_PROPERTIES.map((property: string): string => container.style.getPropertyValue(property)),
    at: performance.now()
  });
};

/**
 * Lays the remembered geometry down on a freshly mounted container and turns the transition on, so
 * the hook's first placement slides from there instead of appearing.
 */
const continueFromRemembered = (key: string, container: HTMLElement): void => {
  const remembered: IRememberedGeometry | undefined = rememberedGeometries.get(key);
  rememberedGeometries.delete(key);

  if (remembered === undefined || performance.now() - remembered.at > CONTINUITY_WINDOW_MS) {
    return;
  }

  container.setAttribute(INDICATOR_STATE_ATTRIBUTE, "instant");
  GEOMETRY_PROPERTIES.forEach((property: string, index: number): void => {
    container.style.setProperty(property, remembered.values[index]);
  });
  // Commit the start geometry before the transition is switched on, or the browser skips to the end.
  container.getBoundingClientRect();
  container.setAttribute(INDICATOR_STATE_ATTRIBUTE, "animate");
};

/** Re-measures the indicator at most once a frame after the watched subtree changes. */
class SelectionWatcher {
  private container: HTMLElement | null = null;
  private observer: MutationObserver | null = null;
  private frame: number | null = null;
  private onChange: () => void = (): void => {};

  public watch(container: HTMLElement | null, onChange: () => void): void {
    this.onChange = onChange;

    if (container === this.container) {
      return;
    }

    this.dispose();
    this.container = container;

    if (container === null || typeof MutationObserver === "undefined") {
      return;
    }

    this.observer = new MutationObserver(this.schedule);
    this.observer.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: WATCHED_ATTRIBUTES
    });
  }

  public dispose(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.container = null;

    if (this.frame !== null) {
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  private readonly schedule = (): void => {
    if (this.frame !== null) {
      return;
    }

    this.frame = window.requestAnimationFrame((): void => {
      this.frame = null;
      this.onChange();
    });
  };
}

/**
 * The one sliding "selected row" fill of a list — rails, panel lists, the Config Manager rail, the
 * component tree. Render it as the FIRST child of the rows' container; its parent is the container
 * (style that with the `kuika-selection-indicator-host` mixin). It measures the selected row with
 * `useSlidingIndicator` and slides between rows on the layout spring (instant under reduced motion).
 *
 * Prefer the rows' own wrapper as the container over a fixed-height scroll viewport: a wrapper
 * resizes when rows move inside it, so the indicator follows a folder opening above the selected row
 * frame by frame. Inside a scroll viewport it still scrolls with the rows.
 *
 * The selection is read from the DOM, not from props: many designer lists re-render their rows
 * without re-rendering the parent that holds the indicator (PureSdComponent's `checkNeedRender`), so
 * the indicator watches its container's subtree — a row's `class`, `data-selected` or
 * `aria-current` changing, rows added, removed or reordered — and re-measures once a frame.
 *
 * It paints above the list's unpositioned backgrounds and below its positioned rows; the host mixin
 * makes the rows positioned and drops their own fills, so the row's text sits on the indicator.
 */
const KuikaSelectionIndicator = ({
  activeSelector = DEFAULT_SELECTION_SELECTOR,
  hidden = false,
  continuityKey,
  className
}: IKuikaSelectionIndicatorProps): JSX.Element => {
  const indicatorRef: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>(null);
  // The container is read through the indicator: a child's layout effects run before its ancestors'
  // refs are attached, so a ref on the container would still be empty here. The indicator's own ref
  // is set, and its parent is already in the document.
  const [containerRef] = useState<React.RefObject<HTMLElement>>((): React.RefObject<HTMLElement> => ({
    get current(): HTMLElement | null {
      return indicatorRef.current?.parentElement ?? null;
    }
  }));
  const prefersReducedMotion: boolean = usePrefersReducedMotion();

  // Declared before the hook on purpose: layout effects run — and are cleaned up — in declaration
  // order, so the remembered geometry is laid down before the hook's first placement, and read on
  // unmount before the hook clears it.
  useLayoutEffect((): (() => void) | undefined => {
    const container: HTMLElement | null = containerRef.current;

    if (continuityKey === undefined || container === null) {
      return undefined;
    }

    if (!prefersReducedMotion) {
      continueFromRemembered(continuityKey, container);
    }

    return (): void => {
      rememberGeometry(continuityKey, container);
    };
  }, [containerRef, continuityKey, prefersReducedMotion]);

  const remeasure: () => void = useSlidingIndicator(containerRef, SELECTION_KEY, { hidden, activeSelector });
  const [watcher] = useState<SelectionWatcher>((): SelectionWatcher => new SelectionWatcher());

  // After every render, but it only re-subscribes when the container element itself changed.
  useLayoutEffect((): void => {
    watcher.watch(containerRef.current, remeasure);
  });

  useLayoutEffect((): (() => void) => {
    return (): void => {
      watcher.dispose();
    };
  }, [watcher]);

  return (
    <span
      ref={indicatorRef}
      className={classNames("kuika-sliding-indicator", "kuika-selection-indicator", className)}
      aria-hidden="true"
    />
  );
};

export default KuikaSelectionIndicator;
